/**
 * google-button.tsx — Nút "Tiếp tục với Google" (Google Identity Services, luồng ID token / popup).
 * Tải https://accounts.google.com/gsi/client một lần; không có client id → không hiện gì.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface GsiCredentialResponse {
  credential: string
}
interface GsiApi {
  accounts: {
    id: {
      initialize: (o: {
        client_id: string
        callback: (r: GsiCredentialResponse) => void
        ux_mode?: 'popup' | 'redirect'
        context?: 'signin' | 'signup' | 'use'
        auto_select?: boolean
        cancel_on_tap_outside?: boolean
      }) => void
      renderButton: (el: HTMLElement, o: Record<string, unknown>) => void
    }
  }
}
declare global {
  interface Window {
    google?: GsiApi
  }
}

const GSI_SRC = 'https://accounts.google.com/gsi/client'
let loader: Promise<GsiApi> | null = null

function loadGsi(): Promise<GsiApi> {
  if (window.google?.accounts?.id) return Promise.resolve(window.google)
  if (!loader) {
    loader = new Promise<GsiApi>((resolve, reject) => {
      const s = document.createElement('script')
      s.src = GSI_SRC
      s.async = true
      s.defer = true
      s.onload = () => (window.google?.accounts?.id ? resolve(window.google) : reject(new Error('gsi')))
      s.onerror = () => {
        loader = null
        reject(new Error('gsi'))
      }
      document.head.appendChild(s)
    })
  }
  return loader
}

export function GoogleButton({
  clientId, onCredential, context = 'signin', disabled, variant = 'standard',
}: {
  clientId: string | null
  onCredential: (credential: string) => void
  context?: 'signin' | 'signup'
  disabled?: boolean
  /** `icon` = nút tròn chỉ có logo G (hàng mạng xã hội trong hộp đăng nhập). */
  variant?: 'standard' | 'icon'
}) {
  const { t } = useTranslation('authPages')
  const ref = useRef<HTMLDivElement>(null)
  const cb = useRef(onCredential)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    cb.current = onCredential
  }, [onCredential])

  useEffect(() => {
    if (!clientId) return
    let alive = true
    loadGsi()
      .then((g) => {
        if (!alive || !ref.current) return
        g.accounts.id.initialize({
          client_id: clientId,
          callback: (r) => r.credential && cb.current(r.credential),
          ux_mode: 'popup',
          context,
          cancel_on_tap_outside: true,
        })
        if (variant === 'icon') {
          g.accounts.id.renderButton(ref.current, { type: 'icon', shape: 'circle', theme: 'outline', size: 'large', locale: 'vi' })
          return
        }
        const width = Math.min(400, Math.max(200, Math.floor(ref.current.getBoundingClientRect().width)))
        g.accounts.id.renderButton(ref.current, {
          type: 'standard', theme: 'outline', size: 'large', shape: 'rectangular',
          text: context === 'signup' ? 'signup_with' : 'continue_with', logo_alignment: 'center', width, locale: 'vi',
        })
      })
      .catch(() => alive && setFailed(true))
    return () => {
      alive = false
    }
  }, [clientId, context, variant])

  if (!clientId) return null
  if (failed) return <p className="text-center text-xs text-muted-foreground">{t('google.loadFailed')}</p>
  if (variant === 'icon') {
    return (
      <div
        ref={ref}
        role="group"
        aria-label={context === 'signup' ? t('google.signupAria') : t('google.signinAria')}
        title="Google"
        className={`flex h-10 w-10 items-center justify-center rounded-full ${disabled ? 'pointer-events-none opacity-60' : ''}`}
      />
    )
  }
  return (
    <div
      ref={ref}
      aria-label={t('google.continueAria')}
      className={`flex min-h-10 w-full justify-center ${disabled ? 'pointer-events-none opacity-60' : ''}`}
    />
  )
}

/** "hoặc" + hàng nút mạng xã hội tròn. Chỉ Google (nhà cung cấp duy nhất đã làm); chưa cấu hình → không hiện gì. */
export function SocialRow({ clientId, onCredential, context = 'signin' }: {
  clientId: string | null
  onCredential: (credential: string) => void
  context?: 'signin' | 'signup'
}) {
  if (!clientId) return null
  return (
    <div className="mt-6 space-y-4">
      <OrDivider />
      <div className="flex justify-center gap-4">
        <GoogleButton clientId={clientId} onCredential={onCredential} context={context} variant="icon" />
      </div>
    </div>
  )
}

export function OrDivider({ label }: { label?: string }) {
  const { t } = useTranslation('common')
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      <span className="h-px flex-1 bg-border" />
      {label ?? t('misc.or')}
      <span className="h-px flex-1 bg-border" />
    </div>
  )
}
